import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import { SupabaseService } from 'src/app/core/services/base/supabase.service';
import { UserService } from 'src/app/core/services/user.service';
import { Attachment } from '../../communication-attachment.component';

export interface PdfNavItem {
  name: string;
  path: string;
  bucket?: string;
  contractId?: string;
  confidential?: boolean;
  readData?: Object;
  onViewed?: () => void;
  preloadedUrl?: string;
}

export interface PdfModalData extends Partial<Attachment> {
  path: string;
  bucket?: string;
  confidential?: boolean;
  contractId?: string;
  readData?: Object;
  onViewed?: () => void;
  preloadedUrl?: string;
  navigationBundle?: PdfNavItem[];
  currentDocumentIndex?: number;
}

@Component({
  selector: 'app-pdf-modal',
  templateUrl: './pdf-modal.component.html',
  styleUrls: ['./pdf-modal.component.scss'],
})
export class PdfModalComponent implements OnInit, OnDestroy {
  zoom: number = 1;
  email?: string;
  documentId?: string;
  legalName?: string;
  url: string = '';
  isConfidential: boolean = false;
  hasBeenViewed: boolean = false;
  el = (event: any) => event.preventDefault();

  buttonDisabled: boolean = true;
  disableButtonBeforeScroll: boolean = true;
  counterBlockButton: number = 0;
  isLoadingConfig: boolean = true;

  private scrollListenerCleanup: (() => void) | null = null;

  minimumSecondsOpen: number = 0;
  isReadRequired: boolean = false;
  isScrollToEndRequired: boolean = false;
  readConfig: any = {};

  currentDocIndex: number = 0;
  totalDocs: number = 0;
  currentDocName: string = '';

  // CPK-4890 — mobile-only "clean viewer": only the document, drag + pinch zoom.
  // Everything below is gated by isMobile, so the desktop experience is untouched.
  isMobile: boolean = this.isMobileDevice();
  fullViewer: boolean = false;
  rotated: boolean = false;
  isLandscapeViewport: boolean = false;
  pinchScale: number = 1;

  private readonly fullViewerMaxLandscapeHeight = 500;
  private readonly minZoom = 0.5;
  private readonly maxZoom = 3;
  private enteredAutomatically: boolean = false;
  private autoEntryDismissed: boolean = false;
  private zoomBeforeFullViewer: number = 1;
  private requestedBrowserFullscreen: boolean = false;
  private pinchStartDistance: number = 0;
  private orientationMql: MediaQueryList | null = null;

  @ViewChild('pdfWrapper', { static: false }) pdfWrapper!: ElementRef;


  constructor(
    public dialogRef: MatDialogRef<PdfModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: PdfModalData,
    private us: UserService,
    private sbs: SupabaseService,
    @Inject(DOCUMENT)
    private document: Document,
    private renderer: Renderer2
  ) {}

  get isLastDocument(): boolean {
    return this.currentDocIndex >= this.totalDocs - 1;
  }

  get hasNavigation(): boolean {
    return this.totalDocs > 1;
  }

  async ngOnInit() {
    this.currentDocIndex = this.data.currentDocumentIndex ?? 0;
    this.totalDocs = this.data.navigationBundle?.length ?? 1;
    this.currentDocName = this.data.navigationBundle?.[this.currentDocIndex]?.name ?? '';

    document.addEventListener('contextmenu', this.el);
    this.watchOrientation();
    if (!this.data.path) {
      this.isLoadingConfig = false;
      return;
    }

    this.isConfidential = this.data.confidential ?? false;
    this.readConfig = this.data.readData ?? {};

    try {
      const user = await this.us.getCurrentUser();
      this.email = user?.primary_email;

      if (this.isConfidential && user?.id) {
        const userWithPii = await this.us.getWithPII(user.id);
        this.documentId = userWithPii?.pii?.id_document_value;
        this.legalName = userWithPii?.pii?.legal_name;
      }

      if (this.data.preloadedUrl) {
        this.url = this.data.preloadedUrl;
      } else {
        const bucket =
          this.data.bucket ||
          (this.data.contractId ? 'contracts' : 'claim-documents');
        const path = this.data.contractId
          ? `${this.data.contractId}/${this.data.path}`
          : this.data.path;
        const { data, error } = await this.sbs.supabase.storage
          .from(bucket)
          .createSignedUrl(path, 60);
        if (error) {
          console.error('[PdfModal] createSignedUrl failed', { path, bucket, error });
        }
        this.url = data?.signedUrl ?? '';
      }
    } catch (err) {
      console.error('[PdfModal] ngOnInit error', err);
    }

    // Proteções anti-burla para documentos confidenciais
    if (this.isConfidential) {

      const objReadConfig = {
        required: this.readConfig.required || 'false',
        isScrollToEnd: this.readConfig.scroll_to_end || 'false',
        minimumSeconds: this.readConfig.nominimun_seconds_open || 0,
      };

      this.isReadRequired = objReadConfig.required === true ? true : false;
      this.isScrollToEndRequired = objReadConfig.isScrollToEnd === true ? true : false;
      this.minimumSecondsOpen = objReadConfig.minimumSeconds;

      this.buttonDisabled = this.minimumSecondsOpen > 0;
      this.disableButtonBeforeScroll = this.isScrollToEndRequired;

      this.setupSecurityMeasures();
      this.enableButtonAfterSeconds(objReadConfig.minimumSeconds);
      // Marcar como visualizado imediatamente
      this.hasBeenViewed = false;
    }

    if(this.isMobileDevice()){
      // The clean viewer may already be open (phone held sideways): it keeps zoom 1.
      this.zoomBeforeFullViewer = 0.8;
      this.zoom = this.fullViewer ? 1 : 0.8;
    };

    this.isLoadingConfig = false;
  }

  get isReadOrScrollRequired(): boolean {
    return this.isReadRequired || this.isScrollToEndRequired || this.minimumSecondsOpen > 0;
  }

  get canMarkCurrentDocumentAsViewed(): boolean {
    return !this.isReadOrScrollRequired || (!this.buttonDisabled && !this.disableButtonBeforeScroll);
  }

  private isMobileDevice(): boolean {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobile = /android|iphone|ipad|ipod|iemobile|blackberry|bb10|mini|mobile|tablet|opera mini|opera mobi|silk/i.test(ua);

    return isMobile;
  }

  private setupSecurityMeasures(): void {

    if(this.isMobileDevice()){
      console.warn('mobile device detected');
      return;
    }

    // Bloquear Print Screen e atalhos de desenvolvedor
    document.addEventListener('keydown', this.preventSecurityBypass);
    document.addEventListener('keyup', this.preventSecurityBypass);

    // Detectar abertura de DevTools
    this.detectDevTools();

    // Bloquear print via CSS
    const style = document.createElement('style');
    style.innerHTML = '@media print { body { display: none !important; } }';
    document.head.appendChild(style);
  }

  private preventSecurityBypass = (e: KeyboardEvent): void => {
    // Bloquear Print Screen
    if (e.key === 'PrintScreen') {
      e.preventDefault();
      this.dialogRef.close(false);
      return;
    }

    // Bloquear Ctrl/Cmd + P (Print)
    if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
      e.preventDefault();
      this.dialogRef.close(false);
      return;
    }

    // Bloquear F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C (DevTools)
    if (
      e.key === 'F12' ||
      ((e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        ['I', 'J', 'C'].includes(e.key.toUpperCase()))
    ) {
      e.preventDefault();
      this.dialogRef.close(false);
      return;
    }

    // Bloquear Ctrl+U (View Source)
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
      e.preventDefault();
      this.dialogRef.close(false);
      return;
    }
  };

  private enableButtonAfterSeconds(secondsToEnable: number) {

    if (!secondsToEnable) {
      console.warn('countdown disabled');
      return;
    }

    this.buttonDisabled = true;
    this.counterBlockButton = secondsToEnable;

    const interval = setInterval(() => {
      this.counterBlockButton--;

      if (this.counterBlockButton <= 0) {
        this.buttonDisabled = false;
        clearInterval(interval);
      }
    }, 1000);
  }

  private detectDevTools(): void {
    // Detectar se DevTools está aberto
    const threshold = 160;

    // const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    // const isMobile = /android|iphone|ipad|ipod|iemobile|blackberry|bb10|mini|mobile|tablet|opera mini|opera mobi|silk/i.test(ua);

    if (this.isMobileDevice()) {
      console.warn('DevTools detection disabled on mobile');
      return;
    }

    const checkDevTools = () => {
      if (
        window.outerWidth - window.innerWidth > threshold ||
        window.outerHeight - window.innerHeight > threshold
      ) {
        this.dialogRef.close(false); //always enabled for prod.
      }
    };

    // Verificar a cada 1 segundo
    const interval = setInterval(checkDevTools, 1000);

    // Limpar intervalo quando destruir componente
    this.dialogRef.afterClosed().subscribe(() => {
      clearInterval(interval);
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isConfidential) {
      this.preventSecurityBypass(event);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('contextmenu', this.el);
    this.orientationMql?.removeEventListener?.('change', this.onOrientationChange);
    this.orientationMql = null;
    document.removeEventListener('fullscreenchange', this.onBrowserFullscreenChange);
    this.exitBrowserFullscreen();
    if (this.isConfidential) {
      document.removeEventListener('keydown', this.preventSecurityBypass);
      document.removeEventListener('keyup', this.preventSecurityBypass);
    }
    this.scrollListenerCleanup?.();
    this.scrollListenerCleanup = null;
  }

  onLoadComplete(pdf: PDFDocumentProxy){

    setTimeout(() => {

      const scrollableEl: HTMLDivElement | null = this.pdfWrapper.nativeElement.querySelector('.ng2-pdf-viewer-container');

      if (!scrollableEl) {
        console.warn('PDF not received.');
        return;
      }

      // Clean up the stale listener from the previous document before registering a new one.
      // Without this, the old listener remains on the same DOM element and can fire during
      // the transient rendering state of the next document (when the container briefly appears
      // empty), prematurely setting disableButtonBeforeScroll = false.
      this.scrollListenerCleanup?.();

      const checkScrollEnd = () => {
        // Use Math.ceil to handle fractional scrollTop values on high-DPI/zoomed displays
        const distanceFromBottom = scrollableEl.scrollHeight - Math.ceil(scrollableEl.scrollTop) - scrollableEl.clientHeight;
        if (distanceFromBottom <= 5) {
          this.disableButtonBeforeScroll = false;
        }
      };

      this.scrollListenerCleanup = this.renderer.listen(scrollableEl, 'scroll', checkScrollEnd);

      // Check immediately in case PDF fits entirely in the viewport (no scroll needed)
      checkScrollEnd();

    }, 1000);
  }


  onClose(): void {
    const navItem = this.data.navigationBundle?.[this.currentDocIndex];
    if (navItem?.onViewed) {
      navItem.onViewed();
    } else if (this.data.onViewed) {
      this.data.onViewed();
    }
    this.dialogRef.close(true);
  }

  onDismiss(): void {
    if (this.canMarkCurrentDocumentAsViewed) {
      this.onClose();
      return;
    }

    this.dialogRef.close(false);
  }

  async goToNextDocument(): Promise<void> {
    // Remove the scroll listener for the current document so it cannot interfere
    // with the scroll state of the next document during its loading transition.
    this.scrollListenerCleanup?.();
    this.scrollListenerCleanup = null;

    const navItem = this.data.navigationBundle?.[this.currentDocIndex];
    if (navItem?.onViewed) {
      navItem.onViewed();
    }

    this.currentDocIndex++;
    const next = this.data.navigationBundle![this.currentDocIndex];

    this.url = '';
    this.isLoadingConfig = true;
    this.buttonDisabled = true;
    this.disableButtonBeforeScroll = true;
    this.counterBlockButton = 0;
    this.hasBeenViewed = false;
    this.currentDocName = next.name;

    if (next.preloadedUrl) {
      this.url = next.preloadedUrl;
    } else {
      const bucket = next.bucket || (next.contractId ? 'contracts' : 'claim-documents');
      const path = next.contractId ? `${next.contractId}/${next.path}` : next.path;
      const { data, error } = await this.sbs.supabase.storage.from(bucket).createSignedUrl(path, 60);
      if (error) {
        console.error('[PdfModal] createSignedUrl failed on navigation', { path, bucket, error });
      }
      this.url = data?.signedUrl ?? '';
    }

    this.readConfig = next.readData ?? {};
    this.isConfidential = next.confidential ?? false;

    if (this.isConfidential) {
      const rc = {
        required: (this.readConfig as any).required ?? false,
        isScrollToEnd: (this.readConfig as any).scroll_to_end ?? false,
        minimumSeconds: (this.readConfig as any).nominimun_seconds_open ?? 0,
      };
      this.isReadRequired = rc.required;
      this.isScrollToEndRequired = rc.isScrollToEnd;
      this.minimumSecondsOpen = rc.minimumSeconds;
      this.buttonDisabled = rc.minimumSeconds > 0;
      this.disableButtonBeforeScroll = rc.isScrollToEnd;
      this.enableButtonAfterSeconds(rc.minimumSeconds);
    } else {
      this.isReadRequired = false;
      this.isScrollToEndRequired = false;
      this.minimumSecondsOpen = 0;
      this.buttonDisabled = false;
      this.disableButtonBeforeScroll = false;
      if (this.isConfidential) {
        document.removeEventListener('keydown', this.preventSecurityBypass);
        document.removeEventListener('keyup', this.preventSecurityBypass);
      }
    }

    this.isLoadingConfig = false;
  }

  incrementZoom(amount: number) {
    this.zoom += amount;
  }

  // ── CPK-4890: mobile clean viewer ────────────────────────────────────────

  private watchOrientation(): void {
    if (!this.isMobile || typeof window.matchMedia !== 'function') {
      return;
    }

    this.orientationMql = window.matchMedia('(orientation: landscape)');
    this.orientationMql.addEventListener?.('change', this.onOrientationChange);
    // A phone already held sideways when the modal opens goes straight to the clean viewer.
    this.applyOrientation(this.orientationMql.matches);
  }

  private onOrientationChange = (event: MediaQueryListEvent): void => {
    this.applyOrientation(event.matches);
  };

  applyOrientation(isLandscape: boolean): void {
    if (!this.isMobile) {
      return;
    }

    this.isLandscapeViewport = isLandscape;
    // The viewport is already sideways: a forced rotation would turn it back upright.
    this.rotated = false;

    if (isLandscape) {
      // Short + landscape = phone. A tablet keeps the regular modal until the button is used.
      if (window.innerHeight <= this.fullViewerMaxLandscapeHeight && !this.fullViewer && !this.autoEntryDismissed) {
        this.enterFullViewer(true);
      }
      this.refreshViewerLayout();
      return;
    }

    this.autoEntryDismissed = false;
    if (this.fullViewer && this.enteredAutomatically) {
      this.leaveFullViewer();
    } else {
      this.refreshViewerLayout();
    }
  }

  enterFullViewer(automatic: boolean = false): void {
    if (!this.isMobile || this.fullViewer) {
      return;
    }

    this.fullViewer = true;
    this.enteredAutomatically = automatic;
    this.zoomBeforeFullViewer = this.zoom;
    // Pages fit the width (zoom-scale page-width); start from 1 and let the user pinch from there.
    this.zoom = 1;

    // The Fullscreen API needs a user gesture (Android) and is missing on iPhone Safari,
    // so the overlay works on its own and the API only hides the browser bars when available.
    if (!automatic) {
      this.requestBrowserFullscreen();
    }
    this.refreshViewerLayout();
  }

  exitFullViewer(): void {
    // Leaving by hand while still sideways must not bounce back in on the next layout change.
    if (this.isLandscapeViewport) {
      this.autoEntryDismissed = true;
    }
    this.leaveFullViewer();
  }

  private leaveFullViewer(): void {
    if (!this.fullViewer) {
      return;
    }

    this.fullViewer = false;
    this.enteredAutomatically = false;
    this.rotated = false;
    this.pinchScale = 1;
    this.pinchStartDistance = 0;
    this.zoom = this.zoomBeforeFullViewer;
    this.exitBrowserFullscreen();
    this.refreshViewerLayout();
  }

  toggleRotation(): void {
    this.rotated = !this.rotated;
    this.refreshViewerLayout();
  }

  /** ng2-pdf-viewer only re-fits the pages on a window resize; the overlay changes size without one. */
  private refreshViewerLayout(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  private requestBrowserFullscreen(): void {
    const root = this.document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    const request = root.requestFullscreen ?? root.webkitRequestFullscreen;
    if (!request) {
      return;
    }

    try {
      this.requestedBrowserFullscreen = true;
      this.document.addEventListener('fullscreenchange', this.onBrowserFullscreenChange);
      const result = request.call(root);
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => {
          this.requestedBrowserFullscreen = false;
        });
      }
    } catch {
      this.requestedBrowserFullscreen = false;
    }
  }

  private exitBrowserFullscreen(): void {
    if (!this.requestedBrowserFullscreen) {
      return;
    }

    this.requestedBrowserFullscreen = false;
    this.document.removeEventListener('fullscreenchange', this.onBrowserFullscreenChange);
    if (this.document.fullscreenElement) {
      this.document.exitFullscreen?.().catch(() => undefined);
    }
  }

  /** The system gesture / back button left browser fullscreen: leave the clean viewer with it. */
  private onBrowserFullscreenChange = (): void => {
    if (this.requestedBrowserFullscreen && !this.document.fullscreenElement) {
      this.requestedBrowserFullscreen = false;
      this.document.removeEventListener('fullscreenchange', this.onBrowserFullscreenChange);
      this.exitFullViewer();
    }
  };

  onViewerTouchStart(event: TouchEvent): void {
    if (this.fullViewer && event.touches.length === 2) {
      this.pinchStartDistance = this.touchDistance(event);
      this.pinchScale = 1;
    }
  }

  onViewerTouchMove(event: TouchEvent): void {
    if (!this.pinchStartDistance || event.touches.length !== 2) {
      return;
    }

    const ratio = this.touchDistance(event) / this.pinchStartDistance;
    // Live feedback is a cheap CSS scale; the pages are only re-rendered once, at touchend.
    this.pinchScale = this.clampZoom(this.zoom * ratio) / this.zoom;
  }

  onViewerTouchEnd(event: TouchEvent): void {
    if (!this.pinchStartDistance || event.touches.length >= 2) {
      return;
    }

    this.zoom = this.clampZoom(this.zoom * this.pinchScale);
    this.pinchScale = 1;
    this.pinchStartDistance = 0;
  }

  private touchDistance(event: TouchEvent): number {
    const [a, b] = [event.touches[0], event.touches[1]];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  private clampZoom(value: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, value));
  }
}
